import React from 'react';

import { InputOTP, InputOTPGroup, InputOTPSlot, Spinner, cn } from '@column/ui';

import { CompletionCheck } from './CompletionCheck';

export interface CodeInputStyleProps {
  isDisabled: boolean;
  isLoading: boolean;
  // When true, blur the segments and draw an animated success check over them,
  // in place of the loading spinner. Mirrors the passkey login button's confirmation.
  isComplete: boolean;
}

export interface CodeInputProps extends Partial<CodeInputStyleProps> {
  onChange?: (value: string) => void;
  fields?: number;
  className?: string;
  type?: 'text' | 'password' | 'number';
  value?: string;
  placeholder?: string;
  autoFocus?: boolean;
  hideCaret?: boolean;
  showPlaceholderOnFocus?: boolean;
  // When set, exposes the field to password managers / OS autofill (e.g. `one-time-code`
  // lets 1Password, Safari and Chrome auto-fill a 2FA code).
  autoComplete?: 'one-time-code';
  name?: string;
  // Stretch the segments to fill the available width instead of capping at their
  // natural size (6 × 44px = 264px). Useful in wider dialogs where a centered,
  // fixed-width field looks lost.
  fullWidth?: boolean;
}

const GROUP_CLASS_NAME = 'bg-[rgb(87_90_100/0.04)]';

const SLOT_CLASS_NAME = cn(
  'my-0 h-10 w-11 border-0 outline-2 outline-offset-0 outline-transparent outline-solid',
  'not-last:after:absolute not-last:after:inset-y-px not-last:after:-right-px not-last:after:w-px not-last:after:bg-[rgb(43_49_67/0.075)] dark:not-last:after:bg-white/10',
  'data-[active=true]:[box-shadow:inset_0_0_0_1px_var(--color-blue-500)]! data-[active=true]:outline-blue-500/30 data-[active=true]:after:hidden'
);

const PWM_BADGE_CLASS_NAME =
  "[&_[data-input-otp][style*='calc(']]:w-[calc(100%+48px)]! [&_[data-input-otp][style*='calc(']]:[clip-path:inset(0_48px_0_0)]!";

export const CodeInput: React.FC<CodeInputProps> = (props) => {
  const fieldNum = props.fields ?? 6;
  const isLoading = props.isLoading ?? false;
  const isComplete = props.isComplete ?? false;
  const isDisabled = props.isDisabled ?? false;
  const fullWidth = props.fullWidth ?? false;
  const isNumeric = props.type === 'number';

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const wasLoading = React.useRef(isLoading);

  // While the code is being verified the field is disabled, which drops the underlying
  // <input>'s DOM focus. input-otp doesn't reliably fire its onBlur for a programmatic
  // disable, so its internal focus state stays stale: when a verify attempt fails and the
  // field is re-enabled (e.g. a wrong code), the first segment still paints the focus ring
  // but keystrokes go nowhere until the user clicks in. Re-focus the input on the
  // loading→idle edge so the user can simply keep typing. Skip it on the success path
  // (isComplete) where the field is intentionally blurred behind the checkmark.
  React.useEffect(() => {
    if (wasLoading.current && !isLoading && !isComplete && !isDisabled) {
      wrapperRef.current?.querySelector<HTMLInputElement>('input[data-input-otp]')?.focus();
    }
    wasLoading.current = isLoading;
  }, [isLoading, isComplete, isDisabled]);

  return (
    <div ref={wrapperRef} className={cn('relative flex justify-center', fullWidth && 'w-full', props.className)}>
      <div
        className={cn(
          'flex justify-center transition-[filter] duration-200',
          PWM_BADGE_CLASS_NAME,
          fullWidth && 'w-full',
          (isLoading || isComplete) && 'blur-[3px]'
        )}
      >
        <InputOTP
          maxLength={fieldNum}
          value={props.value ?? ''}
          onChange={props.onChange}
          disabled={isDisabled || isLoading}
          autoFocus={props.autoFocus}
          name={props.name}
          autoComplete={props.autoComplete}
          pattern={isNumeric ? '^[0-9]*$' : undefined}
          inputMode={isNumeric ? 'numeric' : 'text'}
          containerClassName={cn(fullWidth && 'w-full')}
        >
          <InputOTPGroup className={cn(GROUP_CLASS_NAME, fullWidth && 'w-full')}>
            {Array.from({ length: fieldNum }, (_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className={cn(SLOT_CLASS_NAME, fullWidth && 'w-auto min-w-0 flex-1')}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {isComplete ? (
        <div className="pointer-events-none absolute top-1/2 left-1/2 -mt-2.5 -ml-2.5 size-5 text-seafoam-600 dark:text-seafoam-400">
          <CompletionCheck />
        </div>
      ) : (
        <Spinner
          className={cn(
            'absolute top-1/2 left-1/2 -mt-2.5 -ml-2.5 size-5 transition-[translate,scale,opacity] duration-200',
            !isLoading && 'pointer-events-none -translate-y-3 scale-75 opacity-0'
          )}
        />
      )}
    </div>
  );
};
